import { useEffect, useRef, type ComponentProps, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cx } from './cx';

/**
 * <form> with native validation bubbles turned off (we render our own accessible messages).
 * Pair with react-hook-form: `onSubmit={handleSubmit(save, () => focusFirstInvalid(ref.current))}`.
 */
export function Form({ className, ...props }: ComponentProps<'form'>) {
  return <form noValidate className={cx('flex flex-col gap-6', className)} {...props} />;
}

/** Group of related fields: <fieldset> + <legend> (WCAG 1.3.1). */
export function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={cx('flex min-w-0 flex-col gap-4', className)}>
      <legend className="mb-1 w-full border-b border-dotted border-border pb-1.5 text-lg font-semibold text-heading">
        {title}
      </legend>
      {description && <p className="-mt-2 text-sm text-ink-muted">{description}</p>}
      {children}
    </fieldset>
  );
}

/** Responsive field grid: one column on phones, `columns` from md. */
export function FormGrid({
  columns = 2,
  className,
  ...props
}: ComponentProps<'div'> & { columns?: 1 | 2 | 3 }) {
  return (
    <div
      className={cx(
        'grid grid-cols-1 gap-x-4 gap-y-4',
        columns === 2 && 'md:grid-cols-2',
        columns === 3 && 'md:grid-cols-2 lg:grid-cols-3',
        className,
      )}
      {...props}
    />
  );
}

/** Spans all FormGrid columns. */
export function FormGridFull({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cx('md:col-span-full', className)} {...props} />;
}

/** Submit/cancel row. Buttons stack full-width on phones, primary action last in DOM order. */
export function FormActions({
  className,
  align = 'end',
  ...props
}: ComponentProps<'div'> & { align?: 'start' | 'end' | 'between' }) {
  return (
    <div
      className={cx(
        'flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:items-center',
        align === 'end' && 'sm:justify-end',
        align === 'between' && 'sm:justify-between',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Error summary shown at the top of a form after a failed submit: focused on appearance and
 * each entry links to its field (GOV.UK pattern; WCAG 3.3.1 / 3.3.3).
 */
export function FormErrorSummary({
  errors,
  title = 'Please fix the following problems',
}: {
  errors: readonly { fieldId: string; message: ReactNode }[];
  title?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const count = errors.length;
  useEffect(() => {
    if (count) ref.current?.focus();
  }, [count]);
  if (!count) return null;
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      className="border border-danger-border bg-danger-soft px-4 py-3 text-danger-ink"
    >
      <p className="flex items-center gap-2 font-semibold">
        <AlertCircle aria-hidden="true" className="size-5" />
        {title}
      </p>
      <ul className="mt-1 list-disc ps-9 text-sm">
        {errors.map((e) => (
          <li key={e.fieldId}>
            <a
              href={`#${e.fieldId}`}
              className="text-danger-ink underline"
              onClick={(ev) => {
                ev.preventDefault();
                document.getElementById(e.fieldId)?.focus();
              }}
            >
              {e.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
